import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Check, Calendar, MapPin, Users, Briefcase, Hash, Loader2, ShieldAlert } from "lucide-react";
import { format } from "date-fns";

const MAX_POLLS = 6;
const POLL_INTERVAL_MS = 2500;

export default function Confirmation() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentState, setPaymentState] = useState(sessionId ? "polling" : "ready");
  const pollCountRef = useRef(0);

  const loadBooking = async () => {
    try {
      const { data } = await api.get(`/bookings/${id}`);
      setBooking(data);
      return data;
    } catch {
      setBooking(null);
      return null;
    }
  };

  useEffect(() => {
    (async () => {
      await loadBooking();
      setLoading(false);
    })();
  }, [id]);

  // Poll Stripe status if returning from checkout
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPaymentState("paid");
          await loadBooking();
          return;
        }
        if (data.status === "expired") {
          setPaymentState("expired");
          return;
        }
        pollCountRef.current += 1;
        if (pollCountRef.current >= MAX_POLLS) {
          setPaymentState("timeout");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        pollCountRef.current += 1;
        if (pollCountRef.current >= MAX_POLLS) {
          setPaymentState("timeout");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="min-h-screen pt-32 px-6 text-white/60">Loading…</div>;
  if (!booking) return <div className="min-h-screen pt-32 px-6 text-white/60">Reservation not found.</div>;

  const isPaid = booking.payment_status === "paid" || paymentState === "paid";

  return (
    <div data-testid="confirmation-page" className="min-h-screen bg-[#0A0A0A] pt-32 pb-20">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <StatusHeader state={paymentState} isPaid={isPaid} />

        <div className="border border-white/10 mt-12 p-8 md:p-10 bg-[#141414]">
          <div className="flex items-center justify-between border-b border-white/10 pb-5 flex-wrap gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">Confirmation Code</div>
              <div data-testid="confirmation-code" className="font-mono-display text-2xl text-[#C5A059] mt-1">{booking.confirmation_code}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">{isPaid ? "Paid" : "Total Due"}</div>
              <div className="font-heading text-3xl text-white mt-1" data-testid="conf-total">${(isPaid && booking.paid_total > 0 ? booking.paid_total : booking.estimated_fare).toFixed(2)}</div>
              {booking.tip_amount > 0 && (
                <div className="text-[10px] uppercase tracking-wider text-white/40 mt-1">Includes ${booking.tip_amount.toFixed(2)} gratuity</div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mt-8 text-sm">
            <Detail icon={Hash} label="Vehicle" value={`${booking.vehicle_class} · ${booking.vehicle_name}`} testid="conf-vehicle" />
            <Detail icon={Calendar} label="Pickup Time" value={format(new Date(booking.pickup_datetime), "PPPp")} testid="conf-pickup-time" />
            <Detail icon={MapPin} label="Pickup" value={booking.pickup_address} testid="conf-pickup" />
            {booking.dropoff_address && <Detail icon={MapPin} label="Drop-off" value={booking.dropoff_address} testid="conf-dropoff" />}
            <Detail icon={Users} label="Passengers" value={booking.passengers} testid="conf-passengers" />
            <Detail icon={Briefcase} label="Ride Type" value={booking.ride_type.replace("-", " ")} testid="conf-ride-type" />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-10">
          <Link
            to="/trips"
            data-testid="conf-view-trips-btn"
            className="flex-1 text-center bg-white text-[#0A0A0A] hover:bg-[#C5A059] hover:text-white transition-colors duration-300 px-8 py-4 text-xs uppercase tracking-[0.3em]"
          >
            View My Trips
          </Link>
          <Link
            to="/book"
            data-testid="conf-book-another-btn"
            className="flex-1 text-center border border-white/20 hover:border-white text-white px-8 py-4 text-xs uppercase tracking-[0.3em] transition-colors duration-300"
          >
            Book Another
          </Link>
        </div>
      </div>
    </div>
  );
}

function StatusHeader({ state, isPaid }) {
  if (isPaid) {
    return (
      <>
        <div className="flex items-center justify-center w-16 h-16 mx-auto bg-[#34D399]/15 border border-[#34D399]/40">
          <Check className="w-8 h-8 text-[#34D399]" />
        </div>
        <div className="text-center mt-8">
          <div data-testid="status-label" className="text-[10px] uppercase tracking-[0.3em] text-[#34D399]">Payment Successful · Reservation Confirmed</div>
          <h1 className="font-heading text-4xl sm:text-5xl text-white mt-4">Your chauffeur awaits.</h1>
          <p className="text-[#A3A3A3] mt-4 max-w-lg mx-auto">
            Receipt sent to your account. We'll dispatch your driver and notify you ten minutes before pickup.
          </p>
        </div>
      </>
    );
  }
  if (state === "polling") {
    return (
      <>
        <div className="flex items-center justify-center w-16 h-16 mx-auto bg-[#C5A059]/15 border border-[#C5A059]/40">
          <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin" />
        </div>
        <div className="text-center mt-8">
          <div data-testid="status-label" className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">Verifying Payment</div>
          <h1 className="font-heading text-4xl sm:text-5xl text-white mt-4">One moment…</h1>
          <p className="text-[#A3A3A3] mt-4 max-w-lg mx-auto">We're confirming your secure payment with Stripe.</p>
        </div>
      </>
    );
  }
  if (state === "expired" || state === "timeout") {
    return (
      <>
        <div className="flex items-center justify-center w-16 h-16 mx-auto bg-[#F87171]/15 border border-[#F87171]/40">
          <ShieldAlert className="w-8 h-8 text-[#F87171]" />
        </div>
        <div className="text-center mt-8">
          <div data-testid="status-label" className="text-[10px] uppercase tracking-[0.3em] text-[#F87171]">Payment Pending</div>
          <h1 className="font-heading text-4xl sm:text-5xl text-white mt-4">Awaiting Confirmation</h1>
          <p className="text-[#A3A3A3] mt-4 max-w-lg mx-auto">
            We didn't receive Stripe confirmation yet. If you completed payment, the status will update shortly. Refresh in a minute.
          </p>
        </div>
      </>
    );
  }
  return (
    <>
      <div className="flex items-center justify-center w-16 h-16 mx-auto bg-[#C5A059]/15 border border-[#C5A059]/40">
        <Check className="w-8 h-8 text-[#C5A059]" />
      </div>
      <div className="text-center mt-8">
        <div data-testid="status-label" className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">Reservation Saved</div>
        <h1 className="font-heading text-4xl sm:text-5xl text-white mt-4">Almost there.</h1>
        <p className="text-[#A3A3A3] mt-4 max-w-lg mx-auto">Your reservation is recorded but not yet paid.</p>
      </div>
    </>
  );
}

function Detail({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex gap-3">
      <Icon className="w-4 h-4 text-[#C5A059] mt-0.5 shrink-0" />
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">{label}</div>
        <div data-testid={testid} className="text-white capitalize mt-0.5">{value}</div>
      </div>
    </div>
  );
}
